import React, { useEffect, useRef } from "react";
import { Pressable, StyleSheet } from "react-native";
import { useTheme } from "react-native-paper";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import Svg, { Path } from "react-native-svg";

import { useAriadneLearned } from "../ariadne/learned";
import { setThreadShown } from "../ariadne/thread";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { useGameStore } from "../stores/useGameStoreFacade";
import type { ExtendedTheme } from "../theme/SynapseTheme";
import { spiralPath } from "./board/markGeometry";

/** One turn when she learns (spec §5); 0 under reduced motion. */
export const TURN_MS = 600;

// The spiral at r = 10 spans x in [-8.5, 17.5] (the tail) and y in [-8.5, 8.5].
const R = 10;
const VIEW_BOX = "-10 -10 28.5 20";
const SVG_W = 40;
const SVG_H = 28;
// Where the spiral's centre lands inside the 44px box, so the turn is about it.
const CENTRE = 22;
const UNIT = SVG_W / 28.5;

/**
 * Her mark in the header, where the logo was (spec §5, D4): tapping it shows
 * or hides her thread. Two states only: her colour when the thread shows,
 * grey when hidden. An empty thread (before she has moved, or outside a
 * game) is the base case, not a third state. It turns once when she learns
 * at the end of a game.
 */
const AriadneHeaderMark: React.FC = () => {
  const { colors, customColors } = useTheme() as ExtendedTheme;
  const on = useGameStore((s) => s.pathDisplayMode.ai);
  const learned = useAriadneLearned((s) => s.count);
  const reduced = useReducedMotion();

  const rotation = useSharedValue(0);
  const mounted = useRef(false);
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    if (reduced) return;
    rotation.value = 0;
    rotation.value = withTiming(360, {
      duration: TURN_MS,
      easing: Easing.inOut(Easing.cubic),
    });
  }, [learned, reduced, rotation]);
  const turn = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }));

  const tint = on ? customColors.aiPath : colors.onSurfaceVariant;

  return (
    <Pressable
      accessibilityRole="switch"
      // The aria-* spelling reaches the DOM on web and accessibilityState on native.
      aria-checked={on}
      accessibilityLabel={
        on ? "Hide Ariadne's thread" : "Show Ariadne's thread"
      }
      onPress={() => {
        void setThreadShown(!on);
      }}
      style={styles.box}
      testID="ariadne-header-mark"
    >
      <Animated.View style={[styles.turn, turn]}>
        <Svg width={SVG_W} height={SVG_H} viewBox={VIEW_BOX} style={styles.svg}>
          <Path
            d={spiralPath(R)}
            fill="none"
            stroke={tint}
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            testID="ariadne-header-mark-path"
          />
        </Svg>
      </Animated.View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  // A 44px hit area; the tail runs 4px past it into the gap before the title.
  box: {
    width: 44,
    height: 44,
    marginLeft: 8,
    marginRight: 12,
    overflow: "visible",
  },
  turn: { width: 44, height: 44, overflow: "visible" },
  // The spiral's centre sits 10 units from the SVG's left and top edges.
  svg: {
    position: "absolute",
    left: CENTRE - R * UNIT,
    top: CENTRE - R * (SVG_H / 20),
  },
});

export default AriadneHeaderMark;
